import { ip_back } from "../config";

/**
 * Default exercise metrics (all training exercises).
 * Lead with SPM / combo-min and max rally, then per-kind counts.
 */

export const DEFAULT_EXERCISE_METRIC_KEYS = Object.freeze([
  "strokes_per_minute",
  "combo_iterations_per_minute",
  "max_rally",
  "total_stroke_count",
]);

export const PERFORMANCE_METRIC_DISPLAY_KEYS = Object.freeze([
  "strokes_per_minute",
  "combo_iterations_per_minute",
  "max_rally",
  "combo_max_rally",
  "fh_count",
  "fh_topspin_count",
  "bh_count",
  "bh_topspin_count",
  "total_stroke_count",
  "rally_count",
]);

function authHeaders() {
  const token =
    (typeof window !== "undefined" && localStorage.getItem("token")) || "";
  return token
    ? { Authorization: token, "Content-Type": "application/json" }
    : { "Content-Type": "application/json" };
}

/**
 * @param {string} teId
 * @param {{ sequenceOrder?: number|null, userId?: string|null, playerId?: string|null, signal?: AbortSignal }} [opts]
 */
export async function fetchDefaultExerciseMetrics(teId, opts = {}) {
  if (!teId) return null;
  const q = new URLSearchParams({ te_id: String(teId) });
  if (opts.sequenceOrder != null && Number(opts.sequenceOrder) > 0) {
    q.set("sequence_order", String(opts.sequenceOrder));
  }
  if (opts.userId) q.set("user_id", String(opts.userId));
  if (opts.playerId) q.set("player_id", String(opts.playerId));
  try {
    const resp = await fetch(
      `${ip_back}api/mobileAPI/pose/metrics?${q.toString()}`,
      { method: "GET", headers: authHeaders(), signal: opts.signal }
    );
    const json = await resp.json().catch(() => ({}));
    if (!resp.ok || !json?.status) return null;
    const data = json.data || {};
    return {
      total_stroke_count:
        data.total_stroke_count != null ? Number(data.total_stroke_count) : null,
      max_rally: data.max_rally != null ? Number(data.max_rally) : null,
      strokes_per_minute:
        data.strokes_per_minute != null ? Number(data.strokes_per_minute) : null,
      combo_iterations_per_minute:
        data.combo_iterations_per_minute != null
          ? Number(data.combo_iterations_per_minute)
          : null,
      combo_max_rally:
        data.combo_max_rally != null ? Number(data.combo_max_rally) : null,
      fh_count: data.fh_count != null ? Number(data.fh_count) : null,
      fh_topspin_count:
        data.fh_topspin_count != null ? Number(data.fh_topspin_count) : null,
      bh_topspin_count:
        data.bh_topspin_count != null ? Number(data.bh_topspin_count) : null,
      bh_count: data.bh_count != null ? Number(data.bh_count) : null,
      rally_count: data.rally_count != null ? Number(data.rally_count) : null,
      essai_count: data.essai_count != null ? Number(data.essai_count) : null,
      recipe_kind: data.recipe_kind || null,
      recipe_combo: Boolean(data.recipe_combo),
      kinds: Array.isArray(data.kinds) ? data.kinds : null,
      labels: data.labels || null,
      metrics: Array.isArray(data.metrics) ? data.metrics : null,
      available: Boolean(data.available),
      source: data.source || null,
      te_id: data.te_id || teId,
      sequence_order: data.sequence_order ?? null,
    };
  } catch (err) {
    if (err?.name === "AbortError") throw err;
    return null;
  }
}

/** TE predefined performance metrics (server-resolved kinds). */
export async function fetchTePerformanceMetrics(teId, opts = {}) {
  if (!teId) return null;
  const q = new URLSearchParams();
  if (opts.userId) q.set("user_id", String(opts.userId));
  if (opts.playerId) q.set("player_id", String(opts.playerId));
  const qs = q.toString();
  try {
    const resp = await fetch(
      `${ip_back}api/TE/${encodeURIComponent(teId)}/performance-metrics${
        qs ? `?${qs}` : ""
      }`,
      { method: "GET", headers: authHeaders(), signal: opts.signal }
    );
    const json = await resp.json().catch(() => ({}));
    if (!resp.ok || !json?.status) return null;
    return json.data || null;
  } catch (err) {
    if (err?.name === "AbortError") throw err;
    return null;
  }
}

/** Objective-linked measurable progress for a player. */
export async function fetchPlayerObjectiveMetrics(userId, opts = {}) {
  if (!userId) return null;
  try {
    const resp = await fetch(
      `${ip_back}api/user/${encodeURIComponent(userId)}/objective-metrics`,
      { method: "GET", headers: authHeaders(), signal: opts.signal }
    );
    const json = await resp.json().catch(() => ({}));
    if (!resp.ok || !json?.status) return null;
    return json.data || null;
  } catch (err) {
    if (err?.name === "AbortError") throw err;
    return null;
  }
}

/**
 * Normalize any pose/ball payload into default metric keys.
 */
export function normalizeDefaultExerciseMetrics(raw) {
  if (!raw || typeof raw !== "object") {
    return {
      total_stroke_count: null,
      max_rally: null,
      strokes_per_minute: null,
      combo_iterations_per_minute: null,
      available: false,
    };
  }
  const strokes =
    raw.total_stroke_count ??
    raw.stroke_count ??
    raw.n_strokes ??
    raw.count ??
    null;
  const maxRally =
    raw.max_rally ?? raw.rally_number ?? raw.maxRally ?? null;
  const total_stroke_count =
    strokes != null && Number.isFinite(Number(strokes)) ? Number(strokes) : null;
  const max_rally =
    maxRally != null && Number.isFinite(Number(maxRally))
      ? Number(maxRally)
      : null;
  const strokes_per_minute =
    raw.strokes_per_minute != null && Number.isFinite(Number(raw.strokes_per_minute))
      ? Number(raw.strokes_per_minute)
      : null;
  return {
    total_stroke_count,
    max_rally,
    strokes_per_minute,
    combo_iterations_per_minute:
      raw.combo_iterations_per_minute != null
        ? Number(raw.combo_iterations_per_minute)
        : null,
    combo_max_rally:
      raw.combo_max_rally != null ? Number(raw.combo_max_rally) : null,
    fh_count: raw.fh_count != null ? Number(raw.fh_count) : null,
    fh_topspin_count:
      raw.fh_topspin_count != null ? Number(raw.fh_topspin_count) : null,
    bh_topspin_count:
      raw.bh_topspin_count != null ? Number(raw.bh_topspin_count) : null,
    bh_count: raw.bh_count != null ? Number(raw.bh_count) : null,
    recipe_kind: raw.recipe_kind || null,
    recipe_combo: Boolean(raw.recipe_combo),
    available:
      (total_stroke_count != null && total_stroke_count > 0) ||
      (max_rally != null && max_rally > 0) ||
      (strokes_per_minute != null && strokes_per_minute > 0) ||
      (raw.combo_iterations_per_minute != null &&
        Number(raw.combo_iterations_per_minute) > 0) ||
      (raw.combo_max_rally != null && Number(raw.combo_max_rally) > 0),
  };
}
